import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { age, formatDate, fullName } from '../../shared/lib/format';
import { useDebouncedValue } from '../../shared/lib/useDebouncedValue';
import { inputClass } from '../../shared/ui/TextField';
import { Card, EmptyState, ErrorText, PageHeader, Spinner } from '../../shared/ui/primitives';
import { listDoctorsDetailed } from '../admin/api';
import { useSession } from '../auth/session';
import { listPatients } from './api';
import type { PatientScope } from './types';

export function PatientsListPage() {
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const doctorId = isAdmin ? (params.get('doctorId') ?? '') : '';

  // Врач переключает «Мои / Доступные мне»; администратор видит общий список без переключателя.
  const [scope, setScope] = useState<PatientScope>(isAdmin ? 'all' : 'mine');
  const scopeOptions = ['mine', 'shared'] as const;
  const scopeLabel: Record<PatientScope, string> = {
    mine: 'Мои',
    all: 'Все',
    shared: 'Доступные мне',
  };

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);

  // Имя выбранного врача для заголовка-фильтра.
  const doctors = useQuery({
    queryKey: ['admin', 'doctors'],
    queryFn: listDoctorsDetailed,
    enabled: isAdmin && Boolean(doctorId),
  });
  const doctor = doctors.data?.find((d) => d.id === doctorId);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: doctorId ? ['patients', 'byDoctor', doctorId, search] : ['patients', scope, search],
    queryFn: () =>
      doctorId
        ? listPatients({ doctorId, search: search || undefined })
        : listPatients({ scope, search: search || undefined }),
  });

  const clearDoctor = () => {
    params.delete('doctorId');
    setParams(params, { replace: true });
  };

  return (
    <section className="space-y-5">
      <PageHeader title="Пациенты">
        {isAdmin && (
          <Link
            to="/patients/new"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
          >
            Добавить пациента
          </Link>
        )}
      </PageHeader>

      {doctorId && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2 text-sm">
          <span className="text-muted">Пациенты врача:</span>
          <span className="font-medium text-ink">
            {doctor ? doctor.fullName || doctor.email : '…'}
          </span>
          <button
            type="button"
            onClick={clearDoctor}
            className="ml-1 rounded-md px-2 py-0.5 text-xs font-medium text-accent hover:bg-surface"
          >
            × показать всех
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Поиск по ФИО или телефону"
          className={`${inputClass} sm:max-w-xs`}
        />
        {!doctorId && !isAdmin && (
          <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm">
            {scopeOptions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                  scope === s ? 'bg-primary text-white' : 'text-muted hover:text-ink'
                }`}
              >
                {scopeLabel[s]}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading && <Spinner />}
      {isError && <ErrorText>{apiErrorMessage(error, 'Не удалось загрузить список')}</ErrorText>}
      {data && data.items.length === 0 && <EmptyState>Ничего не найдено.</EmptyState>}

      {data && data.items.length > 0 && (
        <Card className="divide-y divide-line overflow-hidden">
          {data.items.map((p) => {
            const years = age(p.birthDate);
            return (
              <Link
                key={p.id}
                to={`/patients/${p.id}`}
                className="flex flex-col gap-1 px-4 py-3 transition-colors hover:bg-canvas sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              >
                <span className="font-medium text-ink">{fullName(p)}</span>
                <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-muted">
                  <span>{formatDate(p.birthDate)}</span>
                  {years !== null && <span>{years} лет</span>}
                  {p.phone && <span>{p.phone}</span>}
                </span>
              </Link>
            );
          })}
        </Card>
      )}

      {data && <p className="text-xs text-muted">Всего: {data.total}</p>}
    </section>
  );
}
